import React from 'react';
import {
  Button as BaseButton,
  ButtonProps as BaseButtonProps,
} from '@mui/base/Button';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';

export interface CompactButtonProps extends BaseButtonProps {
  active?: boolean;
  variant?: 'primary' | 'secondary';
  buttonStyle?: 'pill' | 'square';
}

const CompactButton = React.forwardRef<HTMLButtonElement, CompactButtonProps>(
  (props, ref) => {
    const { active, className, variant, children, buttonStyle, ...rest } =
      props;
    const { classes, cx } = useStyles();

    return (
      <BaseButton
        ref={ref}
        className={cx(classes.root, className, {
          [classes.secondary]: variant === 'secondary',
          [classes.pill]: buttonStyle === 'pill',
          [classes.active]: active,
        })}
        {...rest}
      >
        {children}
      </BaseButton>
    );
  }
);

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    alignItems: 'center',
    textDecoration: 'none !important',
    boxSizing: 'border-box',
    border: `1px solid ${palette.gray_500}`,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    width: 'fit-content',
    minHeight: theme.spacing(3),
    height: 'auto',
    color: palette.gray_0,
    backgroundColor: palette.gray_800,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: palette.gray_700,
    },
    '&.base--disabled': {
      color: palette.gray_300,
      '&:hover': {
        // Keep original color
        backgroundColor: palette.gray_800,
        cursor: 'not-allowed',
      },
    },
    '&.base--focusVisible': {
      border: `1px solid ${palette.mint_100}`,
      outline: 'none',
    },
  },
  secondary: {
    backgroundColor: palette.gray_1000,
    '&:hover': {
      backgroundColor: palette.gray_900,
    },
    '&.base--disabled': {
      '&:hover': {
        backgroundColor: palette.gray_900,
      },
    },
  },
  pill: {
    borderRadius: theme.spacing(12.5),
  },
  active: {
    border: `1px solid ${palette.mint_300}`,
  },
}));

export default CompactButton;
